import type { ExecutionProgressHydration } from '../../../executionOperations/types';
import { isLoadingStop } from '../../../executionOperations/executionHelpers';
import { buildEffectiveExecutionView } from '../routeExecutionProjectionFacade';
import type { HumanizedCargoMeta } from '../cargoHumanization';
import { enrichExecutionCargo } from '../cargoHumanization';
import type { VehicleCargoItemProjection, VehicleCargoProjection, VehicleCargoState } from './types';

const STATE_LABELS: Record<VehicleCargoState, string> = {
  pending_load: 'Ожидает погрузки',
  loading: 'Погрузка',
  in_transit: 'В машине',
  partially_unloaded: 'Частично выгружен',
  delivered: 'Доставлен'
};

function resolveVehicleCargoState(params: {
  loadedQty: number;
  unloadedQty: number;
  plannedQty: number;
  isDelivered: boolean;
  allLoadingComplete: boolean;
}): VehicleCargoState {
  const { loadedQty, unloadedQty, plannedQty, isDelivered, allLoadingComplete } = params;

  if (isDelivered || (plannedQty > 0 && unloadedQty >= plannedQty)) {
    return 'delivered';
  }
  if (unloadedQty > 0) {
    return 'partially_unloaded';
  }
  if (loadedQty > 0 && allLoadingComplete) {
    return 'in_transit';
  }
  if (loadedQty > 0) {
    return 'loading';
  }
  return 'pending_load';
}

function stopLabel(
  stops: ExecutionProgressHydration['stops'],
  uid: string
): string | null {
  const idx = stops.findIndex((s) => s.executionStopUid === uid);
  if (idx < 0) return null;
  const stop = stops[idx];
  return `${isLoadingStop(stop.type) ? 'Погрузка' : 'Выгрузка'} ${idx + 1}`;
}

export function buildVehicleCargoProjection(params: {
  hydration: ExecutionProgressHydration;
  cargoIndex: Map<number, HumanizedCargoMeta>;
}): VehicleCargoProjection {
  const { hydration, cargoIndex } = params;
  const effective = buildEffectiveExecutionView(hydration);
  const cargoSource = effective?.cargo ?? hydration.cargo;
  const enriched = enrichExecutionCargo(cargoSource, cargoIndex);
  const allLoadingComplete = effective?.progress.allLoadingComplete ?? hydration.progress.allLoadingComplete;
  const vehicleLoad = effective?.projection?.vehicleLoad;

  const items: VehicleCargoItemProjection[] = enriched.map((c) => {
    const human = c.human;
    const currentState = resolveVehicleCargoState({
      loadedQty: c.loadedQty,
      unloadedQty: c.unloadedQty,
      plannedQty: c.plannedQty,
      isDelivered: c.isDelivered,
      allLoadingComplete
    });
    const remainingQty = Math.max(0, c.loadedQty - c.unloadedQty);

    return {
      cargoId: c.cargoId,
      cargoName: human.name,
      loadedQty: c.loadedQty,
      unloadedQty: c.unloadedQty,
      remainingQty,
      plannedQty: c.plannedQty,
      currentState,
      stateLabel: STATE_LABELS[currentState],
      fromStop: stopLabel(hydration.stops, c.fromExecutionStopUid),
      toStop: stopLabel(hydration.stops, c.toExecutionStopUid),
      applicationNumber: human.applicationNumber
    };
  });

  const inVehicle = items.filter(
    (i) => i.currentState === 'in_transit' || i.currentState === 'partially_unloaded'
  );

  const inVehiclePlaces =
    vehicleLoad != null
      ? Math.max(0, vehicleLoad.totalLoaded - vehicleLoad.totalUnloaded)
      : inVehicle.reduce((s, i) => s + i.remainingQty, 0);

  return {
    items,
    inVehiclePlaces,
    inVehicleWeightKg: Math.round(
      inVehicle.reduce((s, i) => {
        const row = enriched.find((e) => e.cargoId === i.cargoId);
        const w = row?.human.weightKg ?? 0;
        const share = row && row.plannedQty > 0 ? i.remainingQty / row.plannedQty : 0;
        return s + w * share;
      }, 0)
    ),
    deliveredPlaces: items
      .filter((i) => i.currentState === 'delivered')
      .reduce((s, i) => s + i.plannedQty, 0)
  };
}

/** Vehicle cargo lines for progress widget — uses in-vehicle semantics, not raw unload %. */
export function buildVehicleCargoProgressLines(
  projection: VehicleCargoProjection
): { loadedLines: string[]; remainingLines: string[] } {
  const loadedLines = projection.items
    .filter((i) => i.loadedQty > 0 && i.currentState !== 'pending_load')
    .map((i) => `${i.cargoName} — ${i.loadedQty} шт (${i.stateLabel})`);

  const remainingLines = projection.items
    .filter((i) => i.currentState === 'in_transit' || i.currentState === 'partially_unloaded')
    .map((i) => `${i.cargoName} — ${i.remainingQty} шт в машине`);

  return { loadedLines, remainingLines };
}
